/**
 * Trusscore Estimator - SlatWall Calculations
 *
 * Calculates SlatWall panel requirements and associated trims.
 *
 * Ported from: calculateSlatWallPanelProducts_Helper() in estimator.js
 */

import { MaterialItem, ProductColor, CoreDimensions, SlatWallConfig } from '../types';
import { getSlatWallProduct, getHTrimProduct } from '../data/productTable';
import {
  SLATWALL_PANEL_LENGTH,
  SLATWALL_COVERAGE_HEIGHT,
  TRIM_STOCK_LENGTH,
} from './constants';

export interface SlatWallOffcut {
  length: number;
  count: number;
}

export interface SlatWallResult {
  panels: MaterialItem[];
  hTrims: MaterialItem[];
  jTrims: MaterialItem[];
  totalPanelCount: number;
}

/**
 * Calculate SlatWall panel requirements.
 *
 * Port of calculateSlatWallPanelProducts_Helper from estimator.js
 *
 * @param slatWallHeight - Total height of SlatWall coverage in feet
 * @param sectionNetLength - Net length of the wall section in feet
 * @param color - SlatWall color
 * @param numPanelRows - Number of 6" SlatWall rows
 * @returns SlatWall panels and associated trims
 */
export function calculateSlatWallPanels(
  slatWallHeight: number,
  sectionNetLength: number,
  color: ProductColor,
  numPanelRows: number,
  availableOffcuts?: SlatWallOffcut[]
): SlatWallResult {
  const panels: MaterialItem[] = [];
  const hTrims: MaterialItem[] = [];
  const jTrims: MaterialItem[] = [];

  if (slatWallHeight <= 0 || sectionNetLength <= 0 || numPanelRows <= 0) {
    return { panels, hTrims, jTrims, totalPanelCount: 0 };
  }

  // Each row independently needs ceil(wallLength / 8) panels
  // Cut pieces from one row CANNOT be joined to cover another row
  // However, offcuts CAN be reused on other walls via availableOffcuts
  const horizontalPanelsPerRow = Math.ceil(sectionNetLength / SLATWALL_PANEL_LENGTH);
  const remainderPerRow = (horizontalPanelsPerRow * SLATWALL_PANEL_LENGTH) - sectionNetLength;

  let freshPanelsNeeded = 0;

  for (let row = 0; row < numPanelRows; row++) {
    if (remainderPerRow > 0 && remainderPerRow < SLATWALL_PANEL_LENGTH && availableOffcuts) {
      // The last piece in this row needs (SLATWALL_PANEL_LENGTH - remainderPerRow) feet
      // Check if an offcut can cover it
      const neededLength = sectionNetLength - ((horizontalPanelsPerRow - 1) * SLATWALL_PANEL_LENGTH);
      const offcutIdx = availableOffcuts.findIndex(o => o.count > 0 && o.length >= neededLength);
      if (offcutIdx >= 0) {
        // Use offcut instead of fresh panel for the last piece
        freshPanelsNeeded += (horizontalPanelsPerRow - 1);
        const usedOffcut = availableOffcuts[offcutIdx];
        usedOffcut.count--;
        if (usedOffcut.count <= 0) availableOffcuts.splice(offcutIdx, 1);
        // The offcut piece itself may produce a smaller offcut
        const newOffcutLength = usedOffcut.length - neededLength;
        if (newOffcutLength > 0) {
          const existing = availableOffcuts.find(o => Math.abs(o.length - newOffcutLength) < 0.01);
          if (existing) existing.count++;
          else availableOffcuts.push({ length: newOffcutLength, count: 1 });
        }
        continue;
      }
    }
    // No offcut available — use all fresh panels
    freshPanelsNeeded += horizontalPanelsPerRow;
    // Generate offcut from the cut on this row
    if (remainderPerRow > 0 && availableOffcuts) {
      const existing = availableOffcuts.find(o => Math.abs(o.length - remainderPerRow) < 0.01);
      if (existing) existing.count++;
      else availableOffcuts.push({ length: remainderPerRow, count: 1 });
    }
  }

  const totalSlatWallPanels = freshPanelsNeeded;

  // Get SlatWall product
  const slatWallProduct = getSlatWallProduct(color);
  if (slatWallProduct && totalSlatWallPanels > 0) {
    panels.push({
      productNumber: slatWallProduct.productNumber,
      productID: slatWallProduct.productID,
      name: slatWallProduct.name,
      color: slatWallProduct.color as ProductColor,
      quantity: totalSlatWallPanels,
      lengthFeet: slatWallProduct.lengthFeet,
      sqft: slatWallProduct.sqft,
      type: 'SlatWall',
    });
  }

  // Calculate H-Trim for SlatWall panel joins
  // Horizontal seams between SlatWall panels
  const numHorizontalSeams = Math.max(0, horizontalPanelsPerRow - 1);
  if (numHorizontalSeams > 0) {
    const hTrimLengthForSeams = numHorizontalSeams * slatWallHeight;
    const hTrimQuantityForSeams = Math.ceil(hTrimLengthForSeams / TRIM_STOCK_LENGTH);

    const hTrimProduct = getHTrimProduct(color);
    if (hTrimProduct && hTrimQuantityForSeams > 0) {
      hTrims.push({
        productNumber: hTrimProduct.productNumber,
        productID: hTrimProduct.productID,
        name: hTrimProduct.name,
        color: hTrimProduct.color as ProductColor,
        quantity: hTrimQuantityForSeams,
        lengthFeet: hTrimProduct.lengthFeet,
        sqft: 0,
        type: 'H_Trim_SW_Join',
      });
    }
  }

  return {
    panels,
    hTrims,
    jTrims,
    totalPanelCount: totalSlatWallPanels,
  };
}

/**
 * Calculate H-Trim for the interface between W&CB and SlatWall.
 *
 * IMPORTANT: H-Trim at SlatWall/W&CB interface ALWAYS uses SlatWall color,
 * regardless of the W&CB color. This ensures consistent trim appearance.
 *
 * @param sectionNetLength - Net length of the wall section
 * @param color - Color for the trim (MUST be SlatWall color)
 * @param hasWCBAbove - Whether there is W&CB above SlatWall
 * @param hasWCBBelow - Whether there is W&CB below SlatWall
 * @returns H-Trim items for SlatWall/W&CB interface
 */
export function calculateSlatWallInterfaceHTrim(
  sectionNetLength: number,
  color: ProductColor,
  hasWCBAbove: boolean,
  hasWCBBelow: boolean
): MaterialItem[] {
  const hTrims: MaterialItem[] = [];

  if (sectionNetLength <= 0) {
    return hTrims;
  }

  // H-Trim at top of SlatWall (interface with W&CB above)
  if (hasWCBAbove) {
    const topInterfaceLength = sectionNetLength;
    const topInterfaceQuantity = Math.ceil(topInterfaceLength / TRIM_STOCK_LENGTH);

    const hTrimProduct = getHTrimProduct(color);
    if (hTrimProduct && topInterfaceQuantity > 0) {
      hTrims.push({
        productNumber: hTrimProduct.productNumber,
        productID: hTrimProduct.productID,
        name: hTrimProduct.name,
        color: hTrimProduct.color as ProductColor,
        quantity: topInterfaceQuantity,
        lengthFeet: hTrimProduct.lengthFeet,
        sqft: 0,
        type: 'H_Trim_SW_Top_Interface',
      });
    }
  }

  // Note: Bottom interface typically doesn't need H-trim as SlatWall
  // sits on top of W&CB or uses J-trim

  return hTrims;
}

/**
 * Calculate all SlatWall materials for a wall.
 *
 * @param coreDims - Core dimensions for the wall
 * @param slatWallConfig - SlatWall configuration
 * @returns All SlatWall-related materials
 */
export function calculateAllSlatWallMaterials(
  coreDims: CoreDimensions,
  slatWallConfig: SlatWallConfig,
  availableOffcuts?: SlatWallOffcut[]
): SlatWallResult {
  if (!slatWallConfig.enabled || coreDims.slatWallCalculatedHeight <= 0) {
    return { panels: [], hTrims: [], jTrims: [], totalPanelCount: 0 };
  }

  // Calculate SlatWall panels
  const panelResult = calculateSlatWallPanels(
    coreDims.slatWallCalculatedHeight,
    coreDims.wallNetLength,
    slatWallConfig.color,
    slatWallConfig.numPanelRows,
    availableOffcuts
  );

  // Calculate interface H-Trims
  const hasWCBAbove = coreDims.wallheightAboveSW > 0;
  const hasWCBBelow = coreDims.wallheightBelowSW > 0;

  const interfaceHTrims = calculateSlatWallInterfaceHTrim(
    coreDims.wallNetLength,
    slatWallConfig.color,
    hasWCBAbove,
    hasWCBBelow
  );

  return {
    panels: panelResult.panels,
    hTrims: [...panelResult.hTrims, ...interfaceHTrims],
    jTrims: [],
    totalPanelCount: panelResult.totalPanelCount,
  };
}

export default {
  calculateSlatWallPanels,
  calculateSlatWallInterfaceHTrim,
  calculateAllSlatWallMaterials,
};
